import React, { useState, useEffect } from 'react';
import { pazientiAPI, mediciAPI } from '../../services/api';
import { templateAPI } from '../../services/templateApi';
import FirmaCanvas from './FirmaCanvas';
import '../../styles/ModuloAnagrafico.css';
import '../../styles/ModulisticaWizard.css';
import '../../styles/FirmaConsenso.css';

/**
 * FirmaConsenso
 * Compilazione e firma dei consensi richiesti dalle prestazioni di una
 * procedura. Se i consensi sono piu' di uno vengono proposti in sequenza.
 *
 * Props:
 *  - pazienteId
 *  - visitaId
 *  - medicoId
 *  - consensi    array di modelli (con eventuale campo _prestazione)
 *  - onClose()
 *  - onComplete(documenti)
 */

/* ---------- Sostituzione segnaposto (anteprima a video) ---------- */

const dataIta = (v) => {
    if (!v) return '';
    const d = new Date(v);
    return isNaN(d) ? '' : d.toLocaleDateString('it-IT');
};

/* `struttura` arriva da fuori e non e' piu' scritto qui.

   Era `'Rimedical S.r.l.'`, cablato. Finiva nel segnaposto {{struttura}}
   DENTRO IL TESTO DI UN CONSENSO CHE IL PAZIENTE FIRMA: su
   un'installazione venduta, quel paziente avrebbe firmato un documento
   che nomina un'altra clinica. Il nome lo dice il server, che lo legge
   dai parametri della struttura. */
const costruisciMappa = (paziente, medico, prestazione, struttura) => ({
    'paziente.nome': paziente?.nome || '',
    'paziente.cognome': paziente?.cognome || '',
    'paziente.nome_cognome': [paziente?.cognome, paziente?.nome].filter(Boolean).join(' '),
    'paziente.codice_fiscale': paziente?.codice_fiscale || '',
    'paziente.data_nascita': dataIta(paziente?.data_nascita),
    'paziente.luogo_nascita': paziente?.luogo_nascita || '',
    'paziente.id': paziente?.id_paziente || '',
    'medico.nome': medico?.nome || '',
    'medico.cognome': medico?.cognome || '',
    'medico.nome_cognome': [medico?.cognome, medico?.nome].filter(Boolean).join(' '),
    'medico.specializzazione': medico?.specializzazione || '',
    'medico.numero_ordine': medico?.numero_ordine || '',
    'prestazione': prestazione || '',
    'data': new Date().toLocaleDateString('it-IT'),
    /* Il ripiego e' neutro, mai il nome di un'altra clinica: se il
       server non risponde si legge «la struttura», che si nota. */
    'struttura': struttura || 'la struttura',
});

const sostituisci = (testo, mappa) => {
    if (!testo) return '';
    return String(testo).replace(/\{\{\s*([\w.]+)\s*\}\}/g, (intero, chiave) => {
        const valore = mappa[chiave];
        return (valore === undefined || valore === '') ? intero : valore;
    });
};

/** Rende **grassetto** e _corsivo_ come elementi React. */
const conFormattazione = (testo) => {
    const pezzi = String(testo).split(/(\*\*[^*]+\*\*|_[^_]+_)/g).filter((p) => p !== '');
    return pezzi.map((pezzo, i) => {
        if (pezzo.startsWith('**') && pezzo.endsWith('**')) {
            return <strong key={i}>{pezzo.slice(2, -2)}</strong>;
        }
        if (pezzo.startsWith('_') && pezzo.endsWith('_')) {
            return <em key={i}>{pezzo.slice(1, -1)}</em>;
        }
        return <React.Fragment key={i}>{pezzo}</React.Fragment>;
    });
};

/* ---------- Componente ---------- */

const FirmaConsenso = ({ pazienteId, visitaId, medicoId, consensi = [], onClose, onComplete }) => {
    const [indice, setIndice] = useState(0);
    const [paziente, setPaziente] = useState(null);
    const [medico, setMedico] = useState(null);
    const [risposte, setRisposte] = useState({});
    const [firmaPaziente, setFirmaPaziente] = useState(null);
    const [firmaMedico, setFirmaMedico] = useState(null);
    const [caricamento, setCaricamento] = useState(true);
    const [struttura, setStruttura] = useState('');

    /* Come si chiama questa clinica lo dice il server: qui non sta piu'
       scritto. La rotta non e' autenticata perche' la usa anche il tablet,
       dove nessun utente e' collegato. */
    useEffect(() => {
        fetch('/api/struttura')
            .then((r) => r.json())
            .then((d) => setStruttura(d?.data?.ragione_sociale || ''))
            .catch(() => {});
    }, []);

    const [invio, setInvio] = useState(false);
    const [errore, setErrore] = useState(null);
    const [archiviati, setArchiviati] = useState([]);

    const corrente = consensi[indice];
    const blocchi = corrente?.contenuto?.blocchi || [];
    const conMedico = corrente?.contenuto?.firme === 'paziente_medico';

    /* ---------- Caricamento anagrafiche ---------- */

    useEffect(() => {
        let attivo = true;

        const carica = async () => {
            try {
                const rp = await pazientiAPI.getById(pazienteId);
                if (attivo) setPaziente(rp.data.data.paziente);

                if (medicoId) {
                    const rm = await mediciAPI.getById(medicoId);
                    if (attivo) setMedico(rm.data?.data?.medico || rm.data?.data || null);
                }
            } catch (e) {
                console.error('Errore caricamento anagrafiche:', e);
                if (attivo) setErrore('Impossibile caricare i dati del paziente');
            } finally {
                if (attivo) setCaricamento(false);
            }
        };

        carica();
        return () => { attivo = false; };
    }, [pazienteId, medicoId]);

    /* ---------- Firma ---------- */

    const valida = () => {
        const mancanti = blocchi
            .map((b, i) => ({ b, i }))
            .filter(({ b, i }) => b.tipo === 'consenso' && risposte[i] === undefined);

        if (mancanti.length) return 'Esprimi una scelta su tutte le caselle di consenso';

        const negatiObbligatori = blocchi
            .map((b, i) => ({ b, i }))
            .filter(({ b, i }) => b.tipo === 'consenso' && b.obbligatorio && risposte[i] !== true);

        if (negatiObbligatori.length) {
            return 'Senza i consensi obbligatori la prestazione non puo essere eseguita';
        }

        if (!firmaPaziente) return 'Manca la firma del paziente';
        if (conMedico && !firmaMedico) return 'Manca la firma del medico';

        return null;
    };

    const firma = async () => {
        const problema = valida();
        if (problema) {
            setErrore(problema);
            return;
        }

        try {
            setInvio(true);
            setErrore(null);

            const risposta = await templateAPI.firma({
                template_id: corrente.id,
                paziente_id: pazienteId,
                visita_id: visitaId || null,
                medico_id: medicoId || null,
                prestazione: corrente._prestazione || null,
                risposte,
                firma_base64: firmaPaziente,
                firma_medico_base64: conMedico ? firmaMedico : null,
                firmato_il: new Date().toISOString(),
                user_agent: navigator.userAgent
            });

            const nuovi = [...archiviati, risposta.data.data.documento];
            setArchiviati(nuovi);

            if (indice < consensi.length - 1) {
                setIndice(indice + 1);
                setRisposte({});
                setFirmaPaziente(null);
                setFirmaMedico(null);
                document.querySelector('.modulo-body')?.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                if (onComplete) onComplete(nuovi);
                onClose();
            }
        } catch (e) {
            console.error('Errore firma consenso:', e);
            setErrore(e?.message || "Errore nell'archiviazione del consenso");
        } finally {
            setInvio(false);
        }
    };

    const saltaTutto = () => {
        if (window.confirm(
            'Chiudere senza firmare i consensi rimanenti?\n\n' +
            'La procedura resta registrata, ma risulterà priva del consenso informato.'
        )) {
            if (onComplete && archiviati.length) onComplete(archiviati);
            onClose();
        }
    };

    /* ---------- Render ---------- */

    if (caricamento) {
        return (
            <div className="modulo-overlay">
                <div className="modulo-finestra fc-finestra">
                    <div className="fc-stato">Caricamento del consenso…</div>
                </div>
            </div>
        );
    }

    if (!corrente) {
        return null;
    }

    const mappa = costruisciMappa(paziente, medico, corrente._prestazione, struttura);

    return (
        <div className="modulo-overlay">
            <div className="modulo-finestra fc-finestra" role="dialog" aria-modal="true">

                <div className="modulo-body">
                    <header className="modulo-intestazione">
                        <div className="intestazione-ragione">RIMEDICAL S.r.l.</div>
                        <div className="intestazione-righe">
                            Via Europa 8 – 47822 Santarcangelo di Romagna (RN)<br />
                            Tel. 0541 620757 – Cell. 352 0453011<br />
                            P.IVA 04538930407 – Cod. Univoco KRRH6B9
                        </div>
                    </header>

                    {consensi.length > 1 && (
                        <div className="fc-avanzamento">
                            Consenso {indice + 1} di {consensi.length}
                        </div>
                    )}

                    <h2 className="modulo-titolo">{sostituisci(corrente.titolo, mappa)}</h2>
                    {corrente._prestazione && (
                        <p className="modulo-nota">Prestazione: {corrente._prestazione}</p>
                    )}

                    <section className="modulo-sezione">
                        {medico && (
                            <div className="fc-medico">
                                <strong>Dott./Dott.ssa {medico.cognome} {medico.nome}</strong>
                                <span>
                                    {[medico.specializzazione,
                                      medico.numero_ordine ? `Iscr. Ordine n. ${medico.numero_ordine}` : null]
                                        .filter(Boolean).join(' · ') || 'Medico esecutore'}
                                </span>
                            </div>
                        )}
                        <div className="riepilogo-paziente">
                            <div className="riepilogo-nome">{paziente?.cognome} {paziente?.nome}</div>
                            <div className="riepilogo-dettagli">
                                nato/a a {paziente?.luogo_nascita || '—'} il {dataIta(paziente?.data_nascita)}
                                {' — C.F. '}{paziente?.codice_fiscale}
                            </div>
                        </div>
                    </section>

                    <section className="modulo-sezione fc-contenuto">
                        {blocchi.map((b, i) => {
                            if (b.tipo === 'sezione') {
                                return <h3 key={i} className="fc-sezione">{sostituisci(b.testo, mappa)}</h3>;
                            }
                            if (b.tipo === 'paragrafo') {
                                return (
                                    <p key={i} className="fc-paragrafo">
                                        {conFormattazione(sostituisci(b.testo, mappa))}
                                    </p>
                                );
                            }
                            if (b.tipo === 'elenco') {
                                const Elenco = b.ordinato ? 'ol' : 'ul';
                                return (
                                    <Elenco key={i} className="fc-elenco">
                                        {(b.voci || []).map((v, j) => (
                                            <li key={j}>{conFormattazione(sostituisci(v, mappa))}</li>
                                        ))}
                                    </Elenco>
                                );
                            }
                            if (b.tipo === 'consenso') {
                                return (
                                    <div key={i} className="voce-consenso">
                                        <h4>
                                            {sostituisci(b.etichetta, mappa)}
                                            {b.obbligatorio && <span className="fc-obbligatorio"> obbligatorio</span>}
                                        </h4>
                                        {b.descrizione && (
                                            <p className="voce-descrizione">{sostituisci(b.descrizione, mappa)}</p>
                                        )}
                                        <div className="scelta-consenso">
                                            <label className={`scelta ${risposte[i] === true ? 'attiva si' : ''}`}>
                                                <input type="radio" checked={risposte[i] === true}
                                                    onChange={() => { setRisposte((p) => ({ ...p, [i]: true })); setErrore(null); }} />
                                                <span>Acconsento</span>
                                            </label>
                                            <label className={`scelta ${risposte[i] === false ? 'attiva no' : ''}`}>
                                                <input type="radio" checked={risposte[i] === false}
                                                    onChange={() => { setRisposte((p) => ({ ...p, [i]: false })); setErrore(null); }} />
                                                <span>Non acconsento</span>
                                            </label>
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })}
                    </section>

                    <section className="modulo-sezione">
                        <h3>{conMedico ? 'Firme' : 'Firma del paziente'}</h3>
                        <div className={conMedico ? 'fc-firme doppia' : 'fc-firme'}>
                            <div className="fc-firma">
                                <span className="fc-firma-etichetta">Firma del paziente</span>
                                <FirmaCanvas
                                    valore={firmaPaziente}
                                    onChange={(v) => { setFirmaPaziente(v); setErrore(null); }}
                                    etichetta={`Santarcangelo di Romagna, ${new Date().toLocaleDateString('it-IT')}`}
                                />
                            </div>
                            {conMedico && (
                                <div className="fc-firma">
                                    <span className="fc-firma-etichetta">Firma del medico</span>
                                    <FirmaCanvas
                                        valore={firmaMedico}
                                        onChange={(v) => { setFirmaMedico(v); setErrore(null); }}
                                        etichetta={medico ? `Dott./Dott.ssa ${medico.cognome}` : 'Medico esecutore'}
                                    />
                                </div>
                            )}
                        </div>
                    </section>

                    {errore && <div className="fc-errore">{errore}</div>}
                </div>

                <footer className="modulo-footer">
                    <button className="btn-annulla" onClick={saltaTutto} disabled={invio}>
                        Firma più tardi
                    </button>
                    <button className="btn-conferma" onClick={firma} disabled={invio}>
                        {invio
                            ? 'Archiviazione…'
                            : indice < consensi.length - 1
                                ? 'Firma e prosegui'
                                : 'Firma e archivia'}
                    </button>
                </footer>

            </div>
        </div>
    );
};

export default FirmaConsenso;
